'use es6';

import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { Layer } from 'foundations-components/transitional/components/Layer';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { delayImportUntilDocumentReady } from 'ContentEditorUI/utils/helpers';
import { Button } from 'foundations-components/transitional/components/Button';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import styled from 'styled-components';
import { VISIBLE_IN_APP_PUBLIC_TO_ALL_INDEXABLE } from 'file-manager-components/constants/fileAccess';
import { EditorMetricsTracker } from 'ContentEditorUI/utils/metricsTracker';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AsyncComponentReload = ({
  retry,
  reportToMetrics,
  componentName
}) => {
  useEffect(() => {
    if (reportToMetrics) {
      EditorMetricsTracker.counter('async-component-failed-to-load', Object.assign({}, componentName ? {
        componentName
      } : {})).increment();
    }
  }, [componentName, reportToMetrics]);
  return /*#__PURE__*/_jsxs(View, {
    inlineSize: "100%",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "ASYNC_COMPONENT_FAILED_TO_LOAD"
    }), /*#__PURE__*/_jsx(ErrorMessage, {
      title: I18n.text('codeSplitting.reload.title'),
      illustrationProps: {
        width: '100px'
      },
      children: retry ? /*#__PURE__*/_jsx(Button, {
        use: "tertiary",
        size: "xs",
        onClick: retry,
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "codeSplitting.retry.message"
        })
      }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "codeSplitting.reload.message"
      })
    })]
  });
};
const makeAsyncComponentWithOverlay = (loader, {
  importName = null,
  delay = 500,
  waitUntilDocumentReadyToLoadDeps = false,
  maxRetries = 1,
  extraProps = {}
} = {}) => {
  let retries = 0;
  if (waitUntilDocumentReadyToLoadDeps) {
    loader = delayImportUntilDocumentReady(loader);
  }
  const FullScreenWrapper = styled.div.withConfig({
    displayName: "makeAsyncUtils__FullScreenWrapper",
    componentId: "sc-1vp2m53-0"
  })(["position:fixed;z-index:1211;top:0;left:0;right:0;bottom:0;"]);
  return Loadable({
    delay,
    loader: (...args) => {
      return loader(...args).then(imported => {
        if (importName) {
          return imported[importName];
        }
        return imported;
      });
    },
    LoadingComponent: ({
      pastDelay
    }) => {
      return pastDelay ? /*#__PURE__*/_jsx(Layer, {
        children: /*#__PURE__*/_jsx(FullScreenWrapper, {
          children: /*#__PURE__*/_jsx(LoadingOverlay, {
            "data-test-id": "loading-async-component"
          })
        })
      }) : null;
    },
    ErrorComponent: props => {
      let ErrorComponent = null;
      if (retries >= maxRetries) {
        ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {
          reportToMetrics: true
        });
      } else {
        ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {
          retry: props.retry,
          reportToMetrics: true
        });
      }
      retries++;
      return /*#__PURE__*/_jsx(Layer, {
        children: /*#__PURE__*/_jsx(View, {
          backgroundColor: "fill-surface-default",
          children: /*#__PURE__*/_jsx(FullScreenWrapper, {
            children: ErrorComponent
          })
        })
      });
    },
    render(module, props) {
      const Component = module.default || module;
      return /*#__PURE__*/_jsx(Component
      // Props from the parent component that is actually mounting this
      , Object.assign({}, props, extraProps));
    }
  });
};
const makeAsyncComponent = (loader, {
  delay = 500,
  waitUntilDocumentReadyToLoadDeps = false,
  maxRetries = 1,
  containerStyles,
  throwErrorOnFail = false,
  extraProps = {},
  LoadingComponent = ({
    pastDelay
  }) => {
    return pastDelay ? /*#__PURE__*/_jsx("div", {
      style: Object.assign({
        height: '100%'
      }, containerStyles),
      children: /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      })
    }) : null;
  }
} = {}) => {
  let retries = 0;
  if (waitUntilDocumentReadyToLoadDeps) {
    loader = delayImportUntilDocumentReady(loader);
  }
  return Loadable({
    delay,
    loader,
    LoadingComponent,
    ErrorComponent: props => {
      if (throwErrorOnFail) {
        throw props.error;
      } else {
        let ErrorComponent = null;
        if (retries >= maxRetries) {
          ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {
            componentName: extraProps.componentName,
            reportToMetrics: true
          });
        } else {
          ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {
            retry: props.retry,
            componentName: extraProps.componentName,
            reportToMetrics: true
          });
        }
        retries++;
        return /*#__PURE__*/_jsx("div", {
          style: {
            height: '100%'
          },
          children: ErrorComponent
        });
      }
    },
    render(module, props) {
      const Component = module.default || module;
      return /*#__PURE__*/_jsx(Component
      // Props from the parent component that is actually mounting this
      , Object.assign({}, props, extraProps));
    }
  });
};
const makeAsyncComponentWithNoStates = (loader, {
  waitUntilDocumentReadyToLoadDeps = false
} = {}) => {
  if (waitUntilDocumentReadyToLoadDeps) {
    loader = delayImportUntilDocumentReady(loader);
  }
  return Loadable({
    loader,
    LoadingComponent: () => null,
    ErrorComponent: props => {
      throw props.error;
    }
  });
};
export const makeErrorThrowingAsyncComponent = (loader, options = {}) => {
  options.throwErrorOnFail = true;
  return makeAsyncComponent(loader, options);
};
const makeAsyncModuleEditFormComponent = (asyncComponentsByType, loader, maxRetries = 1, uploadedFileAccess = VISIBLE_IN_APP_PUBLIC_TO_ALL_INDEXABLE) => {
  let retries = 0;
  return Loadable({
    delay: 200,
    loader,
    render(module, props) {
      const EditForm = module.default;
      return /*#__PURE__*/_jsx(EditForm, Object.assign({
        componentsByType: asyncComponentsByType,
        uploadedFileAccess: uploadedFileAccess
      }, props));
    },
    LoadingComponent({
      pastDelay
    }) {
      const PUSH_SIBLINGS_OFF_SCREEN = '30000px';
      if (pastDelay) {
        return /*#__PURE__*/_jsx("div", {
          style: {
            height: PUSH_SIBLINGS_OFF_SCREEN
          },
          children: /*#__PURE__*/_jsx(LoadingOverlay, {
            contextual: true
          })
        });
      } else {
        return /*#__PURE__*/_jsx("div", {
          style: {
            height: PUSH_SIBLINGS_OFF_SCREEN
          }
        });
      }
    },
    ErrorComponent(props) {
      let ErrorComponent = null;
      if (retries >= maxRetries) {
        ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {
          reportToMetrics: true
        });
      } else {
        ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {
          retry: props.retry,
          reportToMetrics: true
        });
      }
      retries++;
      return /*#__PURE__*/_jsx(View, {
        blockSize: "100%",
        paddingInline: "400",
        children: ErrorComponent
      });
    }
  });
};
export { makeAsyncComponentWithOverlay, makeAsyncComponent, makeAsyncComponentWithNoStates, makeAsyncModuleEditFormComponent };